<template>
  <div class="clients-container">
    <div class="clients-header">
      <h2>Gestion des Clients</h2>
      <p>Créez et gérez vos clients physiques et moraux</p>
    </div>

    <div class="clients-content">
      <!-- Search and Filters -->
      <div class="clients-toolbar">
        <div class="search-section">
          <div class="search-bar">
            <input 
              v-model="searchQuery"
              type="text"
              placeholder="Rechercher par nom, CIN, téléphone..."
              class="search-input"
              @input="debouncedSearch"
            />
            <i class="search-icon">🔍</i>
          </div>
        </div>

        <div class="filters-section">
          <select v-model="typeFilter" class="filter-select" @change="applyFilters">
            <option value="">Tous les types</option>
            <option value="physique">Physique</option>
            <option value="moral">Moral</option>
          </select>
          <select v-model="nationaliteFilter" class="filter-select" @change="applyFilters">
            <option value="">Toutes nationalités</option>
            <option value="marocain">Marocain</option>
            <option value="etranger">Étranger</option>
          </select>
          <button @click="showCreateModal = true" class="btn-primary">
            + Nouveau Client
          </button>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading">
        <p>Chargement des clients...</p>
      </div>

      <!-- Clients Grid -->
      <div v-else-if="clients.length > 0" class="clients-grid">
        <div 
          v-for="client in clients.filter(c => c && c.id)" 
          :key="client.id"
          class="client-card"
        >
          <div class="client-header">
            <h3>{{ client.name || client.nom_complet || client.raison_sociale }}</h3>
            <div class="client-badges">
              <span class="type-badge" :class="client.tenant_type || client.type">
                {{ (client.tenant_type || client.type) === 'physique' ? 'Physique' : 'Moral' }}
              </span>
              <span class="nationalite-badge">
                {{ (client.nationality || client.nationalite) === 'marocain' ? 'Marocain' : 'Étranger' }}
              </span>
            </div>
          </div>

          <div class="client-info">
            <div class="info-row">
              <strong>{{ (client.tenant_type || client.type) === 'physique' ? 'CIN' : 'RC' }}:</strong>
              <span>{{ client.identification || client.cin || client.registre_commerce }}</span>
            </div>
            <div class="info-row">
              <strong>Téléphone:</strong>
              <span>{{ client.phone || client.telephone }}</span>
            </div>
            <div class="info-row" v-if="client.email">
              <strong>Email:</strong>
              <span>{{ client.email }}</span>
            </div>
            <div class="info-row" v-if="client.address || client.adresse">
              <strong>Adresse:</strong>
              <span>{{ client.address || client.adresse }}</span>
            </div>
            <div class="info-row" v-if="client.city">
              <strong>Ville:</strong>
              <span>{{ client.city }}</span>
            </div>
            <div class="info-row" v-if="(client.nationality || client.nationalite) === 'etranger' && (client.country || client.pays)">
              <strong>Pays:</strong>
              <span>{{ client.country || client.pays }}</span>
            </div>
          </div>

          <div class="client-actions">
            <button @click="editClient(client)" class="btn-edit">
              Modifier
            </button>
            <button @click="viewClientContracts(client)" class="btn-info">
              Contrats
            </button>
            <button @click="deleteClient(client)" class="btn-delete">
              Supprimer
            </button>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="empty-state">
        <div class="empty-icon">👥</div>
        <h3>Aucun client trouvé</h3>
        <p>Commencez par créer votre premier client</p>
        <button @click="showCreateModal = true" class="btn-primary">
          Créer un client
        </button>
      </div>

      <!-- Pagination -->
      <div v-if="pagination.total > pagination.per_page" class="pagination">
        <button 
          @click="loadPage(pagination.current_page - 1)"
          :disabled="pagination.current_page <= 1"
          class="pagination-btn"
        >
          Précédent
        </button>
        <span class="pagination-info">
          Page {{ pagination.current_page }} sur {{ Math.ceil(pagination.total / pagination.per_page) }}
        </span>
        <button 
          @click="loadPage(pagination.current_page + 1)"
          :disabled="pagination.current_page >= Math.ceil(pagination.total / pagination.per_page)"
          class="pagination-btn"
        >
          Suivant
        </button>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <div v-if="showCreateModal || showEditModal" class="modal-overlay" @click.self="closeModals">
      <div class="client-modal">
        <div class="modal-header">
          <h3>{{ showEditModal ? 'Modifier le client' : 'Nouveau client' }}</h3>
          <button @click="closeModals" class="close-btn">&times;</button>
        </div>

        <form @submit.prevent="saveClient" class="client-form">
          <div class="form-section">
            <h4>Type de client</h4>
            <div class="radio-group">
              <label>
                <input type="radio" v-model="clientForm.tenant_type" value="physique" />
                Client Physique
              </label>
              <label>
                <input type="radio" v-model="clientForm.tenant_type" value="moral" />
                Client Moral (Entreprise)
              </label>
            </div>
          </div>

          <div class="form-section">
            <h4>Nationalité</h4>
            <div class="radio-group">
              <label>
                <input type="radio" v-model="clientForm.nationality" value="marocain" />
                Marocain
              </label>
              <label>
                <input type="radio" v-model="clientForm.nationality" value="etranger" />
                Étranger
              </label>
            </div>
          </div>

          <div v-if="clientForm.nationality === 'etranger'" class="form-group">
            <label>Pays *</label>
            <select v-model="clientForm.country" required>
              <option value="">Sélectionner un pays</option>
              <option value="France">France</option>
              <option value="Espagne">Espagne</option>
              <option value="Allemagne">Allemagne</option>
              <option value="Italie">Italie</option>
              <option value="États-Unis">États-Unis</option>
              <option value="Canada">Canada</option>
            </select>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>{{ clientForm.tenant_type === 'physique' ? 'Nom complet' : 'Raison sociale' }} *</label>
              <input type="text" v-model="clientForm.name" required />
            </div>
            <div class="form-group">
              <label>{{ clientForm.tenant_type === 'physique' ? 'CIN' : 'Registre de commerce' }} *</label>
              <input type="text" v-model="clientForm.identification" required />
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Téléphone *</label>
              <input type="tel" v-model="clientForm.phone" required />
            </div>
            <div class="form-group">
              <label>Email</label>
              <input type="email" v-model="clientForm.email" />
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Adresse *</label>
              <input type="text" v-model="clientForm.address" required />
            </div>
            <div class="form-group">
              <label>Ville *</label>
              <input type="text" v-model="clientForm.city" required />
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Code postal *</label>
              <input type="text" v-model="clientForm.postal_code" required />
            </div>
          </div>

          <div class="form-actions">
            <button type="button" @click="closeModals" class="btn-secondary">
              Annuler
            </button>
            <button type="submit" class="btn-primary" :disabled="saving">
              {{ saving ? 'Sauvegarde...' : (showEditModal ? 'Modifier' : 'Créer') }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <DeleteConfirmationModal
      :show="showDeleteModal"
      title="Supprimer le client"
      message="Êtes-vous sûr de vouloir supprimer ce client ?"
      :entity-details="deleteModalEntityDetails"
      :loading="deleting"
      :error="deleteError"
      @cancel="showDeleteModal = false"
      @confirm="confirmDelete"
    />
  </div>
</template>

<script>
import clientService from '@/services/clientService'
import DeleteConfirmationModal from '@/components/common/DeleteConfirmationModal.vue'

export default {
  name: 'ClientsView',
  components: {
    DeleteConfirmationModal
  },
  data() {
    return {
      clients: [],
      loading: false,
      saving: false,
      deleting: false,
      deleteError: null,
      searchQuery: '',
      typeFilter: '',
      nationaliteFilter: '',
      searchTimeout: null,
      pagination: {
        current_page: 1,
        per_page: 12,
        total: 0
      },
      
      // Modals
      showCreateModal: false,
      showEditModal: false,
      showDeleteModal: false,
      
      // Forms
      clientForm: this.getEmptyClientForm(),
      clientToDelete: null,
      editingClientId: null
    }
  },
  computed: {
    deleteModalEntityDetails() {
      if (!this.clientToDelete) return []
      
      const clientName = this.clientToDelete.name || 
                        this.clientToDelete.nom_complet || 
                        this.clientToDelete.raison_sociale || 'N/A'
      
      return [
        {
          label: 'Nom',
          value: clientName
        },
        {
          label: 'Type',
          value: this.clientToDelete.type || 'N/A'
        },
        {
          label: 'Email',
          value: this.clientToDelete.email || 'N/A'
        }
      ]
    }
  },
  mounted() {
    this.loadClients()
  },
  methods: {
    getEmptyClientForm() {
      return {
        tenant_type: 'physique',
        nationality: 'marocain',
        name: '',
        identification: '',
        phone: '',
        email: '',
        address: '',
        city: '',
        postal_code: '',
        country: ''
      }
    },

    async loadClients(page = 1) {
      this.loading = true
      try {
        const filters = {}
        if (this.searchQuery) filters.search = this.searchQuery
        if (this.typeFilter) filters.type = this.typeFilter
        if (this.nationaliteFilter) filters.nationalite = this.nationaliteFilter

        const response = await clientService.getClients({ ...filters, page, per_page: this.pagination.per_page })
        
        // Filter out any null or invalid client objects
        this.clients = (response.data || []).filter(client => client && client.id)
        this.pagination = response.pagination || { current_page: 1, per_page: 12, total: 0 }
      } catch (error) {
        console.error('Error loading clients:', error)
        alert('Erreur lors du chargement des clients')
      } finally {
        this.loading = false
      }
    },

    debouncedSearch() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.applyFilters()
      }, 300)
    },

    applyFilters() {
      this.loadClients(1)
    },

    loadPage(page) {
      this.loadClients(page)
    },

    editClient(client) {
      this.editingClientId = client.id
      this.clientForm = { ...client }
      this.showEditModal = true
    },

    deleteClient(client) {
      this.clientToDelete = client
      this.showDeleteModal = true
    },

    async confirmDelete() {
      if (!this.clientToDelete) return
      
      this.deleting = true
      this.deleteError = null
      try {
        await clientService.deleteClient(this.clientToDelete.id)
        this.clients = this.clients.filter(c => c.id !== this.clientToDelete.id)
        this.showDeleteModal = false
        this.clientToDelete = null
      } catch (error) {
        console.error('Error deleting client:', error)
        this.deleteError = error.response?.data?.message || 'Erreur lors de la suppression du client'
      } finally {
        this.deleting = false
      }
    },

    async saveClient() {
      this.saving = true
      try {
        const clientData = { ...this.clientForm }
        
        if (this.showEditModal && this.editingClientId) {
          // Update existing client
          const response = await clientService.updateClient(this.editingClientId, clientData)
          if (response.success) {
            const index = this.clients.findIndex(c => c.id === this.editingClientId)
            if (index !== -1) {
              this.clients[index] = response.data
            }
          }
        } else {
          // Create new client
          const response = await clientService.createClient(clientData)
          if (response.success) {
            this.clients.unshift(response.data)
          }
        }
        
        this.closeModals()
      } catch (error) {
        console.error('Error saving client:', error)
        alert('Erreur lors de la sauvegarde du client')
      } finally {
        this.saving = false
      }
    },

    viewClientContracts(client) {
      // Navigate to contracts view with client filter
      this.$router.push({ 
        name: 'Contrats', 
        query: { client: client.id } 
      })
    },

    closeModals() {
      this.showCreateModal = false
      this.showEditModal = false
      this.showDeleteModal = false
      this.clientForm = this.getEmptyClientForm()
      this.editingClientId = null
      this.clientToDelete = null
      this.deleteError = null
    }
  }
}
</script> 

<style scoped>
.clients-container {
  padding: 32px;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  overflow-x: hidden;
  box-sizing: border-box;
}

.clients-header {
  text-align: center;
  margin-bottom: 32px;
}

.clients-header h2 {
  margin: 0 0 8px 0;
  color: #1f2937;
  font-size: 2rem;
}

.clients-header p {
  margin: 0;
  color: #6b7280;
  font-size: 1.1rem;
}

.clients-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  gap: 24px;
}

.search-section {
  flex: 1;
  max-width: 400px;
}

.search-bar {
  position: relative;
}

.search-input {
  width: 100%;
  padding: 12px 16px 12px 48px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 16px;
}

.search-icon {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
}

.filters-section {
  display: flex;
  gap: 16px;
  align-items: center;
}

.filter-select {
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: white;
  min-width: 150px;
}

.loading {
  text-align: center;
  padding: 64px;
  color: #6b7280;
  font-size: 1.1rem;
}

.clients-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 24px;
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
}

.client-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 24px;
  transition: all 0.2s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.client-card:hover {
  border-color: #3b82f6;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
  transform: translateY(-2px);
}

.client-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
}

.client-header h3 {
  margin: 0;
  color: #1f2937;
  font-size: 1.2rem;
  flex: 1;
}

.client-badges {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.type-badge, .nationalite-badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  text-align: center;
}

.type-badge.physique {
  background: #dbeafe;
  color: #1e40af;
}

.type-badge.moral {
  background: #f3e8ff;
  color: #7c3aed;
}

.nationalite-badge {
  background: #f0fdf4;
  color: #166534;
}

.client-info {
  margin-bottom: 20px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 0.875rem;
}

.info-row strong {
  color: #374151;
  min-width: 80px;
}

.info-row span {
  color: #6b7280;
  text-align: right;
  flex: 1;
}

.client-actions {
  display: flex;
  gap: 8px;
}

.btn-edit, .btn-info, .btn-delete {
  flex: 1;
  padding: 8px 12px;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-edit {
  background: #f3f4f6;
  color: #374151;
}

.btn-edit:hover {
  background: #e5e7eb;
}

.btn-info {
  background: #dbeafe;
  color: #1e40af;
}

.btn-info:hover {
  background: #bfdbfe;
}

.btn-delete {
  background: #fee2e2;
  color: #dc2626;
}

.btn-delete:hover {
  background: #fecaca;
}

.empty-state {
  text-align: center;
  padding: 80px 40px;
  color: #6b7280;
}

.empty-icon {
  font-size: 4rem;
  margin-bottom: 16px;
}

.empty-state h3 {
  margin: 0 0 8px 0;
  color: #374151;
}

.empty-state p {
  margin: 0 0 24px 0;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 32px;
}

.pagination-btn {
  padding: 8px 16px;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 6px;
  cursor: pointer;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-info {
  font-size: 0.875rem;
  color: #6b7280;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}



.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.modal-header h3 {
  margin: 0;
  color: #1f2937;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #6b7280;
}

.client-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.form-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.form-section h4 {
  margin: 0;
  color: #374151;
  font-size: 1rem;
}

.radio-group {
  display: flex;
  gap: 24px;
}

.radio-group label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-group label {
  font-weight: 500;
  color: #374151;
}

.form-group input,
.form-group select {
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 16px;
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
}

.btn-primary, .btn-secondary, .btn-danger {
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary {
  background: #3b82f6;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #2563eb;
}

.btn-primary:disabled {
  background: #9ca3af;
  cursor: not-allowed;
}

.btn-secondary {
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #d1d5db;
}

.btn-secondary:hover {
  background: #e2e8f0;
}

.btn-danger {
  background: #dc2626;
  color: white;
}

.btn-danger:hover:not(:disabled) {
  background: #b91c1c;
}

.btn-danger:disabled {
  background: #9ca3af;
  cursor: not-allowed;
}

.warning {
  color: #dc2626;
  font-size: 0.875rem;
  margin: 8px 0;
}

/* Responsive */
@media (max-width: 768px) {
  .clients-container {
    padding: 16px;
  }
  
  .clients-toolbar {
    flex-direction: column;
    align-items: stretch;
  }
  
  .filters-section {
    justify-content: center;
  }
  
  .clients-grid {
    grid-template-columns: 1fr;
  }
  
  .form-row {
    grid-template-columns: 1fr;
  }
  
  .client-modal {
    padding: 24px;
  }
}
</style> 
